import { useEffect, useRef, useState } from 'react';
import { payments } from '@square/web-sdk';
import type { Card } from '@square/web-sdk';
import { env } from '../env';

interface UseSquareCard {
  /** Whether Square card collection is configured (keys present). */
  enabled: boolean;
  /** The attached Card instance, ready to tokenize. Null until it mounts. */
  card: Card | null;
  ready: boolean;
  error: string | null;
}

/**
 * Loads the Square Web Payments SDK and attaches a Card input into the element
 * with id `containerId`. No-ops (enabled=false) when Square keys aren't set, so
 * checkout falls back to mock mode. Exposes the live Card so the caller can
 * tokenize the card on submit.
 */
export function useSquareCard(containerId: string): UseSquareCard {
  const appId = env.VITE_SQUARE_APPLICATION_ID;
  const locationId = env.VITE_SQUARE_LOCATION_ID;
  const enabled = Boolean(appId && locationId);

  const [card, setCard] = useState<Card | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cardRef = useRef<Card | null>(null);

  useEffect(() => {
    if (!appId || !locationId) return;
    let cancelled = false;

    const init = async (): Promise<void> => {
      try {
        const sdk = await payments(appId, locationId);
        if (sdk === null) {
          if (!cancelled) setError('Could not load the payment form.');
          return;
        }
        const instance = await sdk.card();
        if (cancelled) {
          await instance.destroy();
          return;
        }
        await instance.attach(`#${containerId}`);
        if (cancelled) {
          await instance.destroy();
          return;
        }
        cardRef.current = instance;
        setCard(instance);
        setReady(true);
      } catch {
        if (!cancelled) setError('Could not load the payment form. Refresh and try again.');
      }
    };

    void init();

    return () => {
      cancelled = true;
      const current = cardRef.current;
      cardRef.current = null;
      if (current) void current.destroy();
    };
  }, [appId, locationId, containerId]);

  return { enabled, card, ready, error };
}
